import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Paper, useTheme, Skeleton } from "@mui/material";

interface CveChartProps {
  data: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  onChartClick?: (severity: string) => void;
  loading?: boolean;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  segment: string | null;
  value: number;
}

const CveChart: React.FC<CveChartProps> = ({ data, onChartClick,loading }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    segment: null,
    value: 0
  });
  
  // Define segment data - using original colors
  const segments = [
    { name: "Critical", value: data.critical, color: "#A7000F" },
    { name: "High", value: data.high, color: "#D90316" },
    { name: "Medium", value: data.medium, color: "#E85800" },
    { name: "Low", value: data.low, color: "#F2BF00" }
  ];
  
  // Calculate total
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  
  // Draw the chart on component mount and when data changes
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions based on container
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      
      // Set high resolution canvas
      const scale = window.devicePixelRatio || 1;
      canvas.width = containerWidth * scale;
      canvas.height = containerHeight * scale;
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;
      ctx.scale(scale, scale);
    }
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set dimensions
    const centerX = parseInt(canvas.style.width) / 2;
    const centerY = parseInt(canvas.style.height) / 2;
    const maxRadius = Math.min(centerX, centerY) * 0.85;
    
    // Calculate maximum value for scaling
    const maxValue = Math.max(...segments.map(segment => segment.value));
    
    // Angle per segment (equal spacing - polar area varies the radius, not the angle)
    const anglePerSegment = (Math.PI * 2) / segments.length;
    
    // Store segment angles for hit detection
    const segmentData: { 
      startAngle: number; 
      endAngle: number;
      radius: number;
      name: string; 
      value: number;
      color: string;
    }[] = [];
    
    // Draw segments
    segments.forEach((segment, index) => {
      const startAngle = index * anglePerSegment;
      const endAngle = startAngle + anglePerSegment;
      
      // Calculate radius based on value (proportional to the square root for area to be proportional to value)
      const radius = maxRadius * Math.sqrt(segment.value / maxValue);
      
      // Store segment data for hit detection
      segmentData.push({
        startAngle,
        endAngle,
        radius,
        name: segment.name,
        value: segment.value,
        color: segment.color
      });
      
      // Draw segment
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = segment.color;
      ctx.fill();
      
      // Add stroke
      ctx.strokeStyle = isDarkMode ? '#333' : '#fff';
      ctx.lineWidth = 1;
      ctx.stroke();
    });
    
    // Add mousemove event listener for hit detection
    const handleMouseMove = (e: MouseEvent) => {
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Calculate distance from center
      const dx = x - centerX;
      const dy = y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Calculate angle
      let angle = Math.atan2(dy, dx);
      if (angle < 0) angle += Math.PI * 2; // Convert to 0-2π range
      
      // Find the segment
      let activeSegment = null;
      for (const segment of segmentData) {
        if (angle >= segment.startAngle && angle <= segment.endAngle && distance <= segment.radius) {
          activeSegment = segment;
          break;
        }
      }
      
      if (activeSegment) {
        setTooltip({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          segment: activeSegment.name,
          value: activeSegment.value
        });
      } else {
        setTooltip(prev => ({ ...prev, visible: false }));
      }
    };
    
    // Add mouseout event listener
    const handleMouseOut = () => {
      setTooltip(prev => ({ ...prev, visible: false }));
    };
    
    // Add click event listener for segment click
    const handleClick = (e: MouseEvent) => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const dx = x - centerX;
      const dy = y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      let angle = Math.atan2(dy, dx);
      if (angle < 0) angle += Math.PI * 2;
      for (const segment of segmentData) {
        if (angle >= segment.startAngle && angle <= segment.endAngle && distance <= segment.radius) {
          if (onChartClick) onChartClick(segment.name);
          break;
        }
      }
    };
    
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseout', handleMouseOut);
    canvas.addEventListener('click', handleClick);
    
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseout', handleMouseOut);
      canvas.removeEventListener('click', handleClick);
    };
  }, [data, segments, total, isDarkMode, onChartClick]);
  if (loading) {
    return (
      <Box 
        sx={{ 
          height: 300,
          bgcolor: theme.palette.mode === "dark" ? "#191C24" : "white",
          borderRadius: "12px",
          boxShadow: theme.palette.mode === "dark"
            ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
            : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
        }}
      >
        {/* Header */}
        <Box sx={{ 
          height: 45,
          display: "flex", 
          alignItems: "center",
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="text" width={80} height={20} />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ 
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 2
        }}>
          {/* Chart Area */}
          <Box sx={{ 
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 2
          }}>
            <Skeleton variant="circular" width={120} height={120} />
          </Box>

          {/* Legend Area */}
          <Box sx={{ 
            display: "flex",
            justifyContent: "space-between",
            mt: "auto",
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`
          }}>
            {["Critical", "High", "Medium", "Low"].map((_, index) => (
              <Box key={index} sx={{ 
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5
              }}>
                <Skeleton variant="text" width={40} height={16} />
                <Skeleton variant="text" width={20} height={16} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  
  return (
    <Box sx={{ 
      height: "100%", 
      display: "flex", 
      flexDirection: "column", 
      pb: 0,
      position: "relative",
      bgcolor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    }}>
      {/* Chart header */}
      <Box sx={{ 
        height: 45,
        display: "flex", 
        alignItems: "center",
        px: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
          CVE : {data.total}
        </Typography>
      </Box>
      
      {/* Chart container */}
      <Box
        ref={containerRef}
        className="no-drag clickable-area"
        sx={{
          flex: 1,
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 2,
          height: "180px",
          cursor:"pointer"
        }}
      >
        {data.total === 0 ? (
          <Box sx={{ 
           display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: theme.palette.text.secondary
          }}>
            <Typography  >
              No CVE found
            </Typography>
            {/* <Typography variant="body2">
              Your system is currently free from known vulnerabilities
            </Typography> */}
          </Box>
        ) : (
          <>
            {/* Canvas for polar area chart */}
            <canvas 
              ref={canvasRef}
              className="no-drag clickable-area"
              style={{ 
                maxWidth: "100%", 
                maxHeight: "100%",
                cursor: "pointer"
              }}
            />
            
            {/* Tooltip box - shown only on hover */}
            {tooltip.visible && (
              <Paper
                elevation={3}
                sx={{
                  position: "fixed",
                  top: tooltip.y - 70,
                  left: tooltip.x + 15,
                  padding: "10px 15px",
                  borderRadius: "8px",
                  backgroundColor: isDarkMode ? theme.palette.background.paper : "white",
                  color: theme.palette.text.primary,
                  zIndex: 10,
                  boxShadow: isDarkMode 
                    ? '0 4px 8px rgba(0,0,0,0.5)' 
                    : '0 4px 8px rgba(0,0,0,0.15)',
                  minWidth: "120px"
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                    {tooltip.segment}:
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {tooltip.value}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                    Total:
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {data.total}
                  </Typography>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>

      {/* Legend with vertical dividers */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          // borderTop: `1px solid ${theme.palette.divider}`,
          mt: "auto",
          py: 2
        }}
      >
        <Box sx={{ 
          textAlign: "center", 
          display: "flex", 
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          borderRight: `2px solid #CCCAE7` 
        }}>
        
          <Typography
            variant="body2"
            fontWeight="bold"
            color='#A7000F'
            fontSize='14px'
          >
            Critical
          </Typography>
          <Typography variant="h5"  fontWeight="bold"  fontSize='14px'>
            {data.critical}
          </Typography>
        </Box>

        <Box sx={{ 
          textAlign: "center", 
          display: "flex", 
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          borderRight: `2px solid #CCCAE7` 
        }}>
          
          <Typography
            variant="body2"
            fontWeight={"bold"}
             fontSize='14px'
            color='#D90316'
          >
            High
          </Typography>
          <Typography variant="h5"  fontSize='14px'  fontWeight="bold">
            {data.high}
          </Typography>
        </Box>

        <Box sx={{ 
          textAlign: "center", 
          display: "flex", 
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          borderRight: `2px solid #CCCAE7` 
        }}>
         
          <Typography
            variant="body2"
            color='#E85800'
            fontWeight={"bold"}
             fontSize='14px'
          >
            Medium
          </Typography>
          <Typography variant="h5"  fontSize='14px' fontWeight="bold">
            {data.medium}
          </Typography>
        </Box>

        <Box sx={{ 
          textAlign: "center", 
          display: "flex", 
          flexDirection: "column",
          alignItems: "center",
          flex: 1
        }}>
        
          <Typography
            variant="body2"
            color='#F2BF00'
            fontWeight={"bold"}
             fontSize='14px'
          >
            Low
          </Typography>
          <Typography variant="h5"   fontSize='14px' fontWeight="bold">
            {data.low}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CveChart;
